import type {
	ListWorkflowTodosResponseJSON,
	ListWorkflowsResponseJSON,
	WorkflowSortBy,
	WorkflowStatusFilter,
	WorkflowTodoSortBy,
	WorkflowTodoStatusFilter,
} from "@performyard/service-contracts";

import request, { ErrorResponse, internalStreamRequest } from "./internal-api/internal-base-request";

export type { WorkflowSortBy, WorkflowStatusFilter, WorkflowTodoSortBy, WorkflowTodoStatusFilter };

export type ListWorkflowsParams = {
	sortBy?: WorkflowSortBy;
	sortOrder?: "asc" | "desc";
	limit?: number;
	skip?: number;
	name?: string;
	workflowName?: string;
	subject?: string;
	status?: WorkflowStatusFilter;
	overdueOnly?: boolean;
	unassignedOnly?: boolean;
};

export const getWorkflows = async (params: ListWorkflowsParams = {}): Promise<ListWorkflowsResponseJSON> => {
	const searchParams = new URLSearchParams();
	if (params.sortBy) searchParams.set("sortBy", params.sortBy);
	if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
	if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
	if (params.skip !== undefined) searchParams.set("skip", String(params.skip));
	if (params.name) searchParams.set("name", params.name);
	if (params.workflowName) searchParams.set("workflowName", params.workflowName);
	if (params.subject) searchParams.set("subject", params.subject);
	if (params.status) searchParams.set("status", params.status);
	if (params.overdueOnly) searchParams.set("overdueOnly", "true");
	if (params.unassignedOnly) searchParams.set("unassignedOnly", "true");

	const query = searchParams.toString();
	const [response] = await request<ListWorkflowsResponseJSON>(`/v1/workflows${query ? `?${query}` : ""}`, {
		method: "GET",
	});
	return response;
};

export type WorkflowTodoFilterParams = {
	action?: string;
	subject?: string;
	actor?: string;
	workflow?: string;
	status?: WorkflowTodoStatusFilter;
	dueDateStart?: string;
	dueDateEnd?: string;
	completedDateStart?: string;
	completedDateEnd?: string;
};

export type ListWorkflowTodosParams = WorkflowTodoFilterParams & {
	sortBy?: WorkflowTodoSortBy;
	sortOrder?: "asc" | "desc";
	limit?: number;
	skip?: number;
};

const buildWorkflowTodosSearchParams = (params: ListWorkflowTodosParams): URLSearchParams => {
	const searchParams = new URLSearchParams();
	if (params.sortBy) searchParams.set("sortBy", params.sortBy);
	if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
	if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
	if (params.skip !== undefined) searchParams.set("skip", String(params.skip));
	if (params.action) searchParams.set("action", params.action);
	if (params.subject) searchParams.set("subject", params.subject);
	if (params.actor) searchParams.set("actor", params.actor);
	if (params.workflow) searchParams.set("workflow", params.workflow);
	if (params.status) searchParams.set("status", params.status);
	if (params.dueDateStart) searchParams.set("dueDateStart", params.dueDateStart);
	if (params.dueDateEnd) searchParams.set("dueDateEnd", params.dueDateEnd);
	if (params.completedDateStart) searchParams.set("completedDateStart", params.completedDateStart);
	if (params.completedDateEnd) searchParams.set("completedDateEnd", params.completedDateEnd);
	return searchParams;
};

export const getWorkflowTodos = async (
	params: ListWorkflowTodosParams = {}
): Promise<ListWorkflowTodosResponseJSON> => {
	const query = buildWorkflowTodosSearchParams(params).toString();
	const [response] = await request<ListWorkflowTodosResponseJSON>(`/v1/workflows/todos${query ? `?${query}` : ""}`, {
		method: "GET",
	});
	return response;
};

export const downloadWorkflowTodosCsv = async (params: WorkflowTodoFilterParams = {}): Promise<Blob> => {
	const query = buildWorkflowTodosSearchParams(params).toString();
	const response = await internalStreamRequest(`/v1/workflows/todos/csv${query ? `?${query}` : ""}`, {
		method: "GET",
	});
	if (!response.ok) {
		throw new ErrorResponse(response.statusText || "Bad fetch response", response);
	}
	return response.blob();
};
